import {
    Box,
    Container,
    Typography,
    Stack,
    Paper,
} from '@mui/material';
import { styled } from '@mui/system';
import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';

const MotionPaper = motion(Paper);

const steps = [
    {
        number: '01',
        img: '/assets/icon/dashboard.webp',
        title: 'Panelde Güncelleyin',
        desc: 'Bağış, proje ve kurumsal içerikleri tek merkezden yönetin. Tüm değişiklikler anında kaydedilir.',
    },
    {
        number: '02',
        img: '/assets/icon/window.webp',
        title: 'Sayfaya Yerleştirin',
        desc: 'İçeriği istediğiniz alana sürükleyin ve yerleştirin. Teknik bilgiye ihtiyaç yok.',
    },
    {
        number: '03',
        img: '/assets/icon/internet.webp',
        title: 'Web’de Anında Yayınlanır',
        desc: 'Güncellemeler saniyeler içinde sitenize yansır. Manuel işlem yapmanız gerekmez.',
        highlight: true,
    },
];


const fadeUp = {
    hidden: { opacity: 0, y: 36 },
    visible: (i = 0) => ({
        opacity: 1, y: 0,
        transition: { duration: 0.65, ease: [0.22, 1, 0.36, 1], delay: i * 0.13 },
    }),
};

const SectionBadge = styled(Box)({
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    padding: '8px 16px',
    borderRadius: 50,
    background: 'rgba(8, 145, 178, 0.08)',
    border: '1px solid rgba(8, 145, 178, 0.15)',
    marginBottom: 20,
});

const HowItWorksSection = () => {
    const ref = useRef(null);
    const isInView = useInView(ref, { once: true, margin: '-80px' });
    return (
        <Box ref={ref} sx={{ py: { xs: 10, md: 16 }, background: '#f8fafc', borderTop: '1px solid #f1f5f9', }}>
            <Container maxWidth="lg">

                {/* HEADER */}
                <motion.div variants={fadeUp} initial="hidden" animate={isInView ? 'visible' : 'hidden'}>
                    <Box textAlign="center" mb={{ xs: 6, md: 9 }}>
                        <SectionBadge>
                            <Box
                                sx={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: '50%',
                                    background: '#0891b2',
                                }}
                            />
                            <Typography
                                sx={{
                                    fontSize: '0.8rem',
                                    fontWeight: 600,
                                    color: '#0891b2',
                                    letterSpacing: '0.5px',
                                }}
                            >
                                NASIL ÇALIŞIR?
                            </Typography>
                        </SectionBadge>


                        <Typography
                            variant="h2"
                            sx={{
                                fontWeight: 900,
                                fontSize: { xs: '2.4rem', md: '3rem' },
                                color: '#0f172ad1',
                                mb: 2,
                                letterSpacing: '-0.02em',
                                lineHeight: 1.15,
                            }}
                        >
                            Panelde Güncelleyin,
                            <br />
                            Web’de Anında Yayınlansın
                        </Typography>
                        <Typography sx={{
                            color: '#64748b', fontSize: '1rem',
                            maxWidth: 520, mx: 'auto', lineHeight: 1.75,
                        }}>
                            İçerikleri bir kez yönetin. Web siteniz her zaman güncel,
                            tutarlı ve kontrol altında kalsın.
                        </Typography>
                    </Box>
                </motion.div>

                {/* TIMELINE WRAPPER */}
                <Box sx={{ position: 'relative' }}>

                    {/* CENTER LINE (desktop only) */}
                    <Box
                        sx={{
                            display: { xs: 'none', md: 'block' },
                            position: 'absolute',
                            top: '50%',
                            left: 0,
                            right: 0,
                            height: '2px',
                            background:
                                'linear-gradient(90deg, #03c0ff 0%, #fc8d05 100%)',
                            transform: 'translateY(-50%)',
                            opacity: 0.3,
                        }}
                    />

                    <Stack
                        direction={{ xs: 'column', md: 'row' }}
                        spacing={5}
                    >
                        {steps.map((step, index) => (
                            <MotionPaper
                                key={index}
                                viewport={{ once: true }}
                                elevation={0}
                                sx={{
                                    flex: 1,
                                    p: 6,
                                    borderRadius: 4,
                                    position: 'relative',
                                    background: '#ffffff',
                                    border: '1px solid rgba(0,0,0,0.06)',
                                    transform:
                                        'translate(var(--move-x, 0), var(--move-y, 0))',
                                    transition: 'transform 0.2s ease',
                                }}
                            >
                                {/* STEP NUMBER */}
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <Box
                                        sx={{
                                            width: 42,
                                            height: 42,
                                            borderRadius: 2,
                                            border: '2px solid #e2e8f0',
                                            bgcolor: '#fafafa',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                        }}
                                    >
                                        <Typography sx={{
                                            fontSize: '1rem',
                                            fontWeight: 800,
                                            color: step.highlight
                                                ? '#fc8d05'
                                                : '#64748b',
                                        }}>
                                            {step.number}
                                        </Typography>
                                    </Box>

                                    {/* ICON */}
                                    <Box
                                        sx={{
                                            width: 56,
                                            height: 56,
                                            borderRadius: '16px',
                                            background:
                                                'linear-gradient(135deg, #03c0ff 0%, #fc8d05 100%)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: '#fff',
                                            mb: 3,
                                        }}
                                    >
                                        <Box
                                            component="img"
                                            src={step.img}
                                            alt={step.title}
                                            sx={{
                                                width: 40,
                                                height: 40,
                                                objectFit: 'contain',
                                                filter: 'brightness(0) invert(1)',
                                            }}
                                        />
                                    </Box>
                                </Box>


                                {/* TITLE */}
                                <Typography
                                    variant="h6"
                                    fontWeight={700}
                                    sx={{ mb: 2 }}
                                >
                                    {step.title}
                                </Typography>

                                {/* DESC */}
                                <Typography
                                    sx={{
                                        color: 'text.secondary',
                                        lineHeight: 1.7,
                                    }}
                                >
                                    {step.desc}
                                </Typography>
                            </MotionPaper>
                        ))}
                    </Stack>
                </Box>

                {/* BOTTOM STRONG MESSAGE */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={isInView ? { opacity: 1, y: 0 } : {}}
                    transition={{ delay: 0.5, duration: 0.6 }}
                >
                    <Box
                        sx={{
                            mt: 6,
                            p: 3,
                            borderRadius: 2,
                            background: '#f8fafc',
                            border: '1px solid #e2e8f0',
                            textAlign: 'center',
                        }}
                    >
                        <Typography
                            sx={{
                                fontSize: '1rem',
                                fontWeight: 600,
                                color: '#0f172a',
                                mb: 0.5,
                            }}
                        >
                            Tüm veri akışı otomatik çalışır.
                        </Typography>
                        <Typography
                            sx={{
                                fontSize: '0.9rem',
                                color: '#64748b',
                                maxWidth: 500,
                                mx: 'auto',
                            }}
                        >
                            Panelde güncelleyin — web siteniz saniyeler içinde yenilensin.
                        </Typography>
                    </Box>
                </motion.div>

            </Container>
        </Box>
    );
};

export default HowItWorksSection;